import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import logoMark from '../../../assets/ardealy-mark.svg';
import { AuroraWash } from '../../../components/AuroraWash';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { useVendorProfileApi } from '../../../common/hooks/use-vendor-profile-api.hook';
import { useArdiApi } from '../../../common/hooks/use-ardi-api.hook';
import { useSocialSchedulerApi } from '../../../common/hooks/use-social-scheduler-api.hook';
import { ShopConnection } from '../../../common/hooks/use-commerce-api.hook';
import { signalRunFinished } from '../../../modules/vendor/work/agent-work.store';
import { markOnboardingSkipped } from '../../../modules/vendor/onboarding/onboarding-state';
import { useWorkspaceStore } from '../../../modules/vendor/side-bar/workspace.store';
import { workspaceById } from '../../../modules/vendor/side-bar/workspaces';
import {
    CHAPTERS,
    Goal,
    JourneyStep,
    SOCIAL_PLATFORMS,
    SocialPlatform,
    chapterOf,
    cleanHandle,
    clearProgress,
    nextStep,
    preparationPlan,
    previousStep,
    readProgress,
    routinesFor,
    saveProgress,
    stepAfterWelcome,
    suggestGoals,
} from '../../../modules/vendor/onboarding/journey';
import { PREVIEW_WEBSITE, readJourneyPreview } from '../../../modules/vendor/onboarding/journey-preview';
import { useBusinessLearning } from '../../../modules/vendor/onboarding/use-business-learning';
import { ArdiNotebook } from './ArdiNotebook';
import { GoalsStep, LearningStep, PreparingStep, SocialsStep, StoreStep, WebsiteStep, WelcomeStep, normaliseUrl } from './journey-steps';

/** Steps where the notebook sits beside the question. */
const WITH_NOTEBOOK: JourneyStep[] = ['learning', 'socials', 'store', 'goals'];

/**
 * A vendor's first minutes with Ardealy.
 *
 * Seven short screens, one question each, while Ardi reads the business in
 * the background and shows what it learns beside every question: hello, the
 * website, the reading, the socials it found, the shop it detected, what the
 * vendor came for — and then the workspaces being set up around those
 * answers, before the dashboard opens with all of it in place.
 *
 * Nothing is a gate. "Skip for now" is always there; every question has a
 * way past it; the reading keeps going on the server whatever the vendor does.
 * Progress is remembered per vendor, so a refresh resumes where they were.
 *
 * `?onboarding=preview` plays a scripted reading instead of the real one
 * (`&step=goals` jumps there) — no request is sent, nothing is saved.
 */
export const OnboardingPage: React.FC<{
    onDone?: () => void;
    /**
     * A website the vendor already gave — the self-serve signup asks for it
     * first — so the journey goes from hello straight to the reading.
     */
    knownWebsite?: string;
    /** Whether `knownWebsite` is already on the profile (the reading reads it from there). */
    websiteSaved?: boolean;
}> = ({ onDone, knownWebsite, websiteSaved }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const reduce = useReducedMotion();
    const { user } = useAuthContext() as any;
    const vendorId: string | undefined = user?.vendor?._id || user?.vendor?.id;
    const firstName: string | undefined = user?.firstname?.trim() || undefined;

    const { on: preview, step: jumpTo } = readJourneyPreview(useLocation().search);
    const profiles = useVendorProfileApi();
    const ardi = useArdiApi();
    const social = useSocialSchedulerApi();
    const setWorkspaceGoals = useWorkspaceStore((s) => s.setGoals);

    const saved = useMemo(() => (preview ? null : readProgress(vendorId)), [preview, vendorId]);
    const websiteKnown = Boolean(knownWebsite) && !preview;
    // Never land on the website question when there is none to ask.
    const savedStep = websiteKnown && saved?.step === 'website' ? 'welcome' : saved?.step;
    const [step, setStep] = useState<JourneyStep>(jumpTo ?? savedStep ?? 'welcome');
    const [direction, setDirection] = useState(1);
    const [website, setWebsite] = useState(saved?.website ?? (websiteKnown ? knownWebsite : undefined) ?? (preview ? PREVIEW_WEBSITE : ''));
    const [goals, setGoals] = useState<Goal[]>(saved?.goals ?? (jumpTo === 'preparing' ? ['visualizer', 'business', 'marketing'] : []));
    const [socials, setSocials] = useState<Partial<Record<SocialPlatform, string>>>(saved?.socials ?? {});
    const [touched, setTouched] = useState<Set<SocialPlatform>>(new Set(Object.keys(saved?.socials ?? {}) as SocialPlatform[]));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [shop, setShop] = useState<ShopConnection | null>(null);
    const [meta, setMeta] = useState<{ configured: boolean; connected: string[]; connecting: boolean }>({ configured: false, connected: [], connecting: false });
    const [finishState, setFinishState] = useState<{ saved: boolean; error: string | null }>({ saved: false, error: null });

    // A jump past the reading plays it instantly so the later screens are full.
    const learning = useBusinessLearning({ preview, instant: Boolean(jumpTo && jumpTo !== 'learning') });
    const { notebook } = learning;

    // Resume: a refresh after the website step joins the reading already going.
    const resumed = useRef(false);
    useEffect(() => {
        if (resumed.current) return;
        resumed.current = true;
        const later = step !== 'welcome' && step !== 'website';
        if (preview && (jumpTo || later)) learning.start(website || PREVIEW_WEBSITE);
        else if (!preview && later && website) learning.resume(normaliseUrl(website));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Remember where they are (never in preview).
    useEffect(() => {
        if (preview || step === 'preparing') return;
        saveProgress(vendorId, { step, website, goals, socials, startedAt: saved?.startedAt ?? new Date().toISOString() });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, website, goals, socials, preview, vendorId]);

    // Handles Ardi found fill the fields the vendor has not typed in.
    useEffect(() => {
        setSocials((current) => {
            const next = { ...current };
            for (const p of SOCIAL_PLATFORMS) {
                const found = notebook.socials[p];
                if (found && !touched.has(p) && !next[p]) next[p] = found.handle;
            }
            return next;
        });
    }, [notebook.socials, touched]);

    // Whether Meta can be connected from here, and what already is.
    useEffect(() => {
        if (preview) {
            setMeta((m) => ({ ...m, configured: true }));
            return;
        }
        social
            .getMetaOAuthStatus()
            .then((s) => setMeta((m) => ({ ...m, configured: Boolean(s?.configured) })))
            .catch(() => undefined);
        refreshAccounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshAccounts = useCallback(async () => {
        if (preview) return [];
        try {
            const list = await social.listAccounts({ limit: 20 });
            const names = (list?.items || []).filter((a: any) => a.status !== 'revoked').map((a: any) => a.accountName || a.platform);
            setMeta((m) => ({ ...m, connected: names }));
            return names;
        } catch {
            return [];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preview]);

    /*
     * Meta in a popup, so the journey stays where it is.
     *
     * The OAuth callback returns to the social scheduler page — in the popup,
     * where it does no harm — and the journey notices the new account by
     * polling the list, then closes the popup.
     */
    const connectMeta = async () => {
        if (preview) {
            setMeta((m) => ({ ...m, connected: ['Casa Ceramica'] }));
            return;
        }
        setMeta((m) => ({ ...m, connecting: true }));
        try {
            const { authUrl } = await social.startMetaOAuth();
            const popup = window.open(authUrl, 'ardealy-meta', 'width=620,height=760');
            const started = Date.now();
            const poll = window.setInterval(async () => {
                const names = await refreshAccounts();
                const closed = !popup || popup.closed;
                if (names.length || closed || Date.now() - started > 5 * 60_000) {
                    window.clearInterval(poll);
                    if (names.length && popup && !popup.closed) popup.close();
                    setMeta((m) => ({ ...m, connecting: false }));
                }
            }, 2000);
        } catch {
            setMeta((m) => ({ ...m, connecting: false }));
        }
    };

    const go = (to: JourneyStep) => {
        setDirection(to === previousStep(step, { websiteKnown }) ? -1 : 1);
        setError(null);
        setStep(to);
    };

    /** The website is saved first: the reading reads it from the profile. */
    const submitWebsite = async () => {
        const url = normaliseUrl(website);
        if (preview) {
            learning.start(url);
            go('learning');
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await profiles.update({ websiteUrl: url });
            learning.start(url);
            go('learning');
        } catch (e) {
            setError((e as Error)?.message || t('ONBOARDING.ERROR_GENERIC'));
        } finally {
            setBusy(false);
        }
    };

    /**
     * Hello → the reading, for a website given at signup. Saved on the profile
     * first if it is not there yet; if that fails, the website question is
     * the way through after all.
     */
    const readKnownWebsite = async () => {
        const url = normaliseUrl(knownWebsite || website);
        setWebsite(url);
        // Back to hello and forward again: join the reading, never buy a second one.
        if (learning.started) {
            go('learning');
            return;
        }
        if (!websiteSaved) {
            setBusy(true);
            try {
                await profiles.update({ websiteUrl: url });
            } catch {
                setBusy(false);
                go('website');
                return;
            }
            setBusy(false);
        }
        learning.start(url);
        go('learning');
    };

    const submitSocials = async () => {
        const clean = Object.fromEntries(SOCIAL_PLATFORMS.map((p) => [p, cleanHandle(socials[p] || '')]).filter(([, v]) => v));
        if (!preview && Object.keys(clean).length) {
            setBusy(true);
            try {
                await profiles.update({ socials: clean });
            } catch {
                /* not worth stopping the journey for; the brand page asks again */
            } finally {
                setBusy(false);
            }
        }
        go('store');
    };

    const suggested = useMemo(() => suggestGoals(notebook), [notebook]);
    const routines = useMemo(() => routinesFor(goals), [goals]);
    const plan = useMemo(() => preparationPlan(goals, notebook, { shopConnected: Boolean(shop), routines }), [goals, notebook, shop, routines]);

    /*
     * The setting-up, for real, while the checklist ticks.
     *
     * Goals and the finished flag onto the profile; the routines each goal
     * implies, skipping any the vendor already has; the switcher reordered.
     * A routine that fails is not a reason to hold the vendor here — the
     * Routines page offers the same templates.
     */
    const finish = useCallback(async () => {
        setFinishState({ saved: false, error: null });
        setWorkspaceGoals(goals);
        if (preview) {
            window.setTimeout(() => setFinishState({ saved: true, error: null }), 600);
            return;
        }
        try {
            await profiles.update({ goals, onboarded: true });
        } catch (e) {
            setFinishState({ saved: false, error: (e as Error)?.message || t('ONBOARDING.ERROR_GENERIC') });
            return;
        }
        try {
            const existing = await ardi.listRoutines();
            const have = new Set((existing?.routines || []).map((r) => r.template).filter(Boolean));
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            for (const template of routines) {
                if (have.has(template)) continue;
                await ardi.createRoutine({ template, schedule: { timezone } as any }).catch(() => undefined);
            }
        } catch {
            /* routines are a convenience here */
        }
        setFinishState({ saved: true, error: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [goals, routines, preview]);

    useEffect(() => {
        if (step === 'preparing') finish();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    /** Out to the dashboard, releasing the gate that rendered this screen. */
    const leave = (completed: boolean) => {
        if (!preview) {
            markOnboardingSkipped(vendorId);
            if (completed) clearProgress(vendorId);
        }
        if (notebook.marketDone) signalRunFinished();
        onDone?.();
        navigate('/home/dashboard');
    };

    const chapter = chapterOf(step);
    const side = WITH_NOTEBOOK.includes(step);
    const hue = goals[0] ? workspaceById(goals[0]).hue : 0;
    const slide = reduce ? 0 : 28 * direction;

    return (
        <div className="fixed inset-0 z-[60] flex flex-col overflow-hidden bg-background" role="dialog" aria-modal aria-label={t('ONBOARDING_V2.TITLE') as string} data-testid="onboarding-journey">
            {/* The brand wash, turning to the first chosen workspace's colour. */}
            <AuroraWash className="pointer-events-none top-0 left-0 h-[300px] w-[520px] opacity-70" hue={hue} />

            <header className="relative z-10 flex items-center gap-4 px-5 py-4 sm:px-8">
                <img src={logoMark} alt="Ardealy" className="size-7 object-contain brightness-0 dark:invert" />
                <ol className="mx-auto hidden items-center gap-2 sm:flex" aria-label={t('ONBOARDING_V2.PROGRESS') as string}>
                    {CHAPTERS.map((c, i) => (
                        <li key={c.id} className="flex items-center gap-2">
                            <span
                                className={cn(
                                    'flex items-center gap-1.5 text-xs transition-colors',
                                    i === chapter ? 'font-medium text-foreground' : i < chapter ? 'text-foreground/70' : 'text-muted-foreground/60',
                                )}
                                aria-current={i === chapter ? 'step' : undefined}
                            >
                                <span className={cn('size-1.5 rounded-full transition-colors', i <= chapter ? 'bg-foreground' : 'bg-muted-foreground/30')} />
                                {t(`ONBOARDING_V2.CHAPTER.${c.id.toUpperCase()}`)}
                            </span>
                            {i < CHAPTERS.length - 1 && <span className="h-px w-6 bg-border" aria-hidden />}
                        </li>
                    ))}
                </ol>
                {step !== 'preparing' && (
                    <Button variant="ghost" size="sm" className="ms-auto text-muted-foreground sm:ms-0" onClick={() => leave(false)} data-testid="journey-skip">
                        {t('ONBOARDING_V2.SKIP')}
                    </Button>
                )}
            </header>

            {/* A hairline that fills chapter by chapter, for phones where the labels are hidden. */}
            <div className="h-px w-full bg-border sm:hidden">
                <div className="h-px bg-foreground transition-[width] duration-500" style={{ width: `${((chapter + 1) / CHAPTERS.length) * 100}%` }} />
            </div>

            <main className="relative z-10 flex-1 overflow-y-auto">
                <div className={cn('mx-auto flex min-h-full w-full items-center px-5 py-10 sm:px-8', side ? 'max-w-[1120px]' : 'max-w-[960px]')}>
                    <div className={cn('grid w-full items-start gap-10', side && 'lg:grid-cols-[minmax(0,1fr)_420px]')}>
                        <AnimatePresence mode="wait" custom={direction} initial={false}>
                            <motion.div
                                key={step}
                                initial={{ opacity: 0, x: slide }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -slide }}
                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                className={cn('w-full', side && 'lg:pt-6')}
                            >
                                {step === 'welcome' && (
                                    <WelcomeStep name={firstName} onNext={() => (websiteKnown ? readKnownWebsite() : go(stepAfterWelcome()))} />
                                )}
                                {step === 'website' && (
                                    <WebsiteStep website={website} setWebsite={setWebsite} busy={busy} error={error} onBack={() => go('welcome')} onNext={submitWebsite} />
                                )}
                                {step === 'learning' && <LearningStep notebook={notebook} onNext={() => go(nextStep('learning'))} />}
                                {step === 'socials' && (
                                    <SocialsStep
                                        notebook={notebook}
                                        values={socials}
                                        setValue={(p, v) => {
                                            setTouched((s) => new Set(s).add(p));
                                            setSocials((s) => ({ ...s, [p]: v }));
                                        }}
                                        meta={{ ...meta, connect: connectMeta }}
                                        busy={busy}
                                        onBack={() => go(previousStep('socials', { websiteKnown }))}
                                        onNext={submitSocials}
                                    />
                                )}
                                {step === 'store' && (
                                    <StoreStep notebook={notebook} connected={shop} onConnected={setShop} onBack={() => go('socials')} onNext={() => go('goals')} />
                                )}
                                {step === 'goals' && <GoalsStep goals={goals} setGoals={setGoals} suggested={suggested} onBack={() => go('store')} onNext={() => go('preparing')} />}
                                {step === 'preparing' && (
                                    <PreparingStep
                                        name={firstName}
                                        brandName={notebook.brand.name}
                                        items={plan}
                                        saved={finishState.saved}
                                        error={finishState.error}
                                        onRetry={finish}
                                        onFinish={() => leave(true)}
                                    />
                                )}
                            </motion.div>
                        </AnimatePresence>

                        {/* On narrow screens the notebook follows the question instead of sitting beside it. */}
                        {side && (
                            <div className="lg:hidden">
                                <ArdiNotebook notebook={notebook} compact />
                            </div>
                        )}
                        {side && (
                            <motion.div
                                className="hidden lg:block"
                                initial={{ opacity: 0, x: reduce ? 0 : 24 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <ArdiNotebook notebook={notebook} className="max-h-[calc(100svh-9rem)]" />
                            </motion.div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default OnboardingPage;
